'use client'

import { useSearchParams } from 'next/navigation'
import { useCallback, useState } from 'react'

import { Button } from '@/components/ui/button'
import { resolveAuthLanding } from '@/constants/authRoutes'
import { isDesktopViewport } from '@/utilities/viewport'

import type { OAuthProvider } from '@/app/actions/auth'

const POPUP_WIDTH = 500
const POPUP_HEIGHT = 600

const PROVIDERS: OAuthProvider[] = ['GoogleOAuth', 'MicrosoftOAuth', 'AppleOAuth']

const oauthIcons = {
  GoogleOAuth: () => (
    <svg className="size-5" viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  ),
  MicrosoftOAuth: () => (
    <svg className="size-5" viewBox="0 0 24 24">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#00A4EF" d="M13 1h10v10H13z" />
      <path fill="#7FBA00" d="M1 13h10v10H1z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  ),
  AppleOAuth: () => (
    <svg className="size-5" viewBox="0 0 24 24">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  ),
}

type OAuthSectionProps = {
  redirectTo?: string | null
}

export function OAuthSection({ redirectTo: redirectToProp }: OAuthSectionProps = {}) {
  const searchParams = useSearchParams()
  const redirectTo = resolveAuthLanding(redirectToProp ?? searchParams.get('redirectTo'))
  const [isLoading, setIsLoading] = useState(false)
  const usePopup = isDesktopViewport()

  const handleSuccessLogin = useCallback(() => {
    window.location.assign(redirectTo)
  }, [redirectTo])

  const handleOAuthSignIn = useCallback(
    async (provider: OAuthProvider) => {
      setIsLoading(true)
      const oauthUrl = usePopup
        ? `/api/auth/oauth?popup=1&provider=${provider}`
        : `/api/auth/oauth?provider=${provider}`
      const response = await fetch(oauthUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        console.error('Failed to get auth URL')
        setIsLoading(false)
        return
      }

      const { url } = await response.json()

      if (usePopup) {
        const left = Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2)
        const top = Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2)
        const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
        const popup = window.open(url, 'oauth', features)

        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return
          if (event.data?.type === 'oauth-complete' && event.data?.success) {
            window.removeEventListener('message', handleMessage)
            setIsLoading(false)
            void handleSuccessLogin()
          }
        }
        window.addEventListener('message', handleMessage)
        const checkClosed = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(checkClosed)
            window.removeEventListener('message', handleMessage)
            void handleSuccessLogin()
            setIsLoading(false)
          }
        }, 300)
      } else {
        window.location.href = url
      }
    },
    [handleSuccessLogin, usePopup],
  )

  return (
    <div className="flex flex-row justify-center gap-2">
      {PROVIDERS.map((provider) => {
        const Icon = oauthIcons[provider]
        return (
          <Button
            key={provider}
            type="button"
            variant="outline"
            className="w-fit"
            onClick={() => handleOAuthSignIn(provider)}
            disabled={isLoading}
            icon={false}
          >
            {isLoading ? 'Loading...' : <Icon />}
          </Button>
        )
      })}
    </div>
  )
}
